import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, Spinner } from "@/components/shared";
import { Users, BookMarked, Map, ClipboardCheck, HeartHandshake, CreditCard, TrendingUp, Repeat, ArrowUpRight, ArrowDownRight } from "lucide-react";

function Stat({ icon: Icon, label, value, tint }) {
  return (
    <div className="kid-card p-6" data-testid={`stat-${label.replace(/\s+/g, "-").toLowerCase()}`}>
      <div className="w-11 h-11 rounded-xl grid place-items-center mb-3" style={{ background: tint }}><Icon size={20} className="text-white" /></div>
      <div className="text-3xl font-extrabold text-kid-navy font-display">{value}</div>
      <div className="text-sm text-kid-muted mt-0.5">{label}</div>
    </div>
  );
}

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [rev, setRev] = useState(null);
  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data));
    api.get("/admin/revenue").then((r) => setRev(r.data)).catch(() => setRev({ coupons: [] }));
  }, []);
  if (!stats) return <Spinner />;
  return (
    <div>
      <PageHeader eyebrow="Admin" title="KidPath Operations" subtitle="Overview of families, content and the Parent Confidence Score." />
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        <Stat icon={Users} label="Registered families" value={stats.total_users} tint="var(--teal)" />
        <Stat icon={CreditCard} label="Paid (Family Path)" value={stats.paid_users} tint="var(--grape)" />
        <Stat icon={Map} label="Active roadmaps" value={stats.active_roadmaps} tint="var(--sky)" />
        <Stat icon={ClipboardCheck} label="Intakes completed" value={stats.intakes_completed} tint="var(--sun)" />
        <Stat icon={BookMarked} label="Active programs" value={stats.active_programs} tint="var(--green)" />
        <Stat icon={HeartHandshake} label="Parent Confidence Score" value={`${stats.confidence_score}%`} tint="var(--coral)" />
      </div>
      <p className="text-sm text-kid-muted mt-6">Parent Confidence Score = % of families answering “Yes” or “Somewhat” to “Do you feel clearer about your next steps?” ({stats.feedback_count} responses).</p>

      {/* Revenue snapshot */}
      <div className="mt-10" data-testid="revenue-snapshot">
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp size={18} className="text-kid-teal" />
          <h2 className="font-display text-xl font-extrabold text-kid-navy">Revenue snapshot</h2>
          <span className="text-xs text-kid-muted">Family Path subscriptions ({rev?.currency || "CAD"})</span>
        </div>
        {!rev ? <Spinner label="Loading revenue…" /> : (
          <>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
              <Stat icon={CreditCard} label="Active subscribers" value={rev.active_subscribers ?? 0} tint="var(--grape)" />
              <Stat icon={TrendingUp} label="MRR" value={money(rev.mrr)} tint="var(--teal)" />
              <Stat icon={Repeat} label="ARR" value={money(rev.arr)} tint="var(--sky)" />
              <div className="kid-card p-6" data-testid="stat-30d-trend">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-11 h-11 rounded-xl grid place-items-center" style={{ background: (rev.net_30d ?? 0) >= 0 ? "var(--green)" : "var(--coral)" }}>
                    {(rev.net_30d ?? 0) >= 0 ? <ArrowUpRight size={20} className="text-white" /> : <ArrowDownRight size={20} className="text-white" />}
                  </div>
                  <span className="text-xs uppercase tracking-wide font-poppins text-kid-muted">Last 30 days</span>
                </div>
                <div className="text-3xl font-extrabold text-kid-navy font-display">
                  {(rev.net_30d ?? 0) > 0 ? "+" : ""}{rev.net_30d ?? 0}
                  <span className="text-sm font-semibold text-kid-muted"> net</span>
                </div>
                <div className="text-sm text-kid-muted mt-1 flex items-center gap-3" data-testid="trend-breakdown">
                  <span className="inline-flex items-center gap-1 text-[#3f9d5c] font-semibold"><ArrowUpRight size={14} /> {rev.new_30d ?? 0} new</span>
                  <span className="inline-flex items-center gap-1 text-kid-coral font-semibold"><ArrowDownRight size={14} /> {rev.cancelled_30d ?? 0} cancelled</span>
                </div>
              </div>
            </div>
            <p className="text-sm text-kid-muted mt-3" data-testid="revenue-breakdown">
              {rev.monthly_subscribers ?? 0} monthly · {rev.annual_subscribers ?? 0} annual. MRR counts annual plans at 1/12 of their yearly price.
            </p>

            {/* Redemptions per code */}
            <h3 className="font-display font-bold text-kid-navy mt-8 mb-3">Redemptions per code</h3>
            {(!rev.coupons || rev.coupons.length === 0) ? (
              <div className="kid-card p-6 text-center text-sm text-kid-muted" data-testid="revenue-coupons-empty">
                No coupons yet. Create discount codes in the Coupons section.
              </div>
            ) : (
              <div className="kid-card overflow-hidden" data-testid="revenue-coupons-table">
                <table className="w-full text-sm">
                  <thead className="bg-kid-mist text-kid-muted text-left"><tr>
                    <th className="px-5 py-3 font-poppins font-semibold">Coupon</th>
                    <th className="px-5 py-3">Code</th>
                    <th className="px-5 py-3">Discount</th>
                    <th className="px-5 py-3">Redeemed</th>
                  </tr></thead>
                  <tbody>
                    {rev.coupons.map((c, i) => (
                      <tr key={i} className="border-t border-kid-line" data-testid="revenue-coupon-row">
                        <td className="px-5 py-3 font-medium text-kid-navy">{c.name}</td>
                        <td className="px-5 py-3">{c.promo_code ? <span className="font-mono text-xs font-semibold px-2 py-1 rounded-lg bg-kid-teal/10 text-kid-teal">{c.promo_code}</span> : <span className="text-kid-muted text-xs">No code</span>}</td>
                        <td className="px-5 py-3 text-kid-navy font-semibold">{c.discount}</td>
                        <td className="px-5 py-3 text-kid-muted">{c.times_redeemed}{c.max_redemptions ? ` / ${c.max_redemptions}` : ""}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
